import { useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, ShoppingCart, Gift, Zap, Shield, Headphones, Star, Package, Home as HomeIcon, Shirt, Car, PawPrint, Tag } from 'lucide-react';
import Navbar, { CATEGORY_TREE } from '@/components/storefront/Navbar';
import HeroSection from '@/components/storefront/HeroSection';
import ProductCard from '@/components/storefront/ProductCard';
import CartDrawer from '@/components/storefront/CartDrawer';
import Footer from '@/components/storefront/Footer';
import CustomerFeedback from '@/components/storefront/CustomerFeedback';
import AnnouncementBar from '@/components/storefront/AnnouncementBar';
import type { Product } from '@/data/allProducts';
import { useProducts } from '@/hooks/useProducts';
import { useCart } from '@/providers/CartProvider';
import { Loader2 } from 'lucide-react';

// ── Coming Soon & Release Radar local images (import.meta.glob) ───
const COMING_SOON_MODULES = import.meta.glob(
  '/src/assets/coming-soon/*.{jpg,jpeg,png,webp}',
  { eager: true, query: '?url', import: 'default' }
) as Record<string, string>;

const ALL_COMING_SOON: string[] = Object.keys(COMING_SOON_MODULES)
  .sort()
  .map(k => COMING_SOON_MODULES[k])
  .filter(Boolean);

// First 5 → Coming Soon, next 5 → Release Radar
const COMING_SOON_IMGS  = ALL_COMING_SOON.slice(0, 5);
const RELEASE_RADAR_IMGS = ALL_COMING_SOON.slice(5, 10);

// ── Gift card tier data (from GiftCards.tsx) ─────────────────────
const allAssets = import.meta.glob('@/assets/*.{jpg,jpeg,png,webp}', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const assetUrl = (name: string): string => {
  const key = Object.keys(allAssets).find(k => k.endsWith(`/${name}`));
  return key ? allAssets[key] : '';
};

const GIFT_TIERS = [
  { id: 'midnight',  name: 'Midnight Gift Card',  tagline: 'A subtle, understated gesture that still carries the heavy weight of true exclusivity.', front: 'gift-card-1-front.webp', accent: '#3b5bdb', label: 'Tier I' },
  { id: 'sterling',  name: 'Sterling Gift Card',  tagline: 'A sharper, refined standard that transcends the ordinary and embraces timeless elegance.', front: 'gift-card-2-front.webp', accent: '#94a3b8', label: 'Tier II' },
  { id: 'aurum',     name: 'Aurum Gift Card',     tagline: 'Rooted in ancient history, Aurum reclaims the authentic, heavy weight of true gold.', front: 'gift-card-3-front.webp', accent: '#f59e0b', label: 'Tier III' },
  { id: 'obsidian',  name: 'Obsidian Gift Card',  tagline: 'Step beyond the expected with a striking, mysterious depth that traditional gifts cannot match.', front: 'gift-card-4-front.webp', accent: '#9333ea', label: 'Tier IV' },
  { id: 'sovereign', name: 'Sovereign Gift Card', tagline: 'The undisputed apex of the collection, reflecting an unbreakable bond of absolute trust and loyalty.', front: 'gift-card-5-front.webp', accent: '#dc2626', label: 'Tier V · The Pinnacle' },
];

// ── Helpers ───────────────────────────────────────────────────────
// Same 7 top-level categories as the navbar's icon set, just sized for
// homepage tiles instead of inline nav text. Falls back to a generic tag
// icon for any L1 category not listed here, so adding a new top-level
// category later never breaks this strip — it just shows a plain icon.
const HOME_CATEGORY_ICONS: Record<string, ReactNode> = {
  'Home': <HomeIcon className="h-6 w-6" />,
  'Electronics': <Zap className="h-6 w-6" />,
  'Fashion, Jewelry & Accessories': <Shirt className="h-6 w-6" />,
  'Leisure': <Star className="h-6 w-6" />,
  'Automotive': <Car className="h-6 w-6" />,
  'Consumables': <Package className="h-6 w-6" />,
  'Pets': <PawPrint className="h-6 w-6" />,
};

function SectionHeader({ title, subtitle, to, badge }: { title: string; subtitle: string; to?: string; badge?: string }) {
  return (
    <div className="flex items-end justify-between mb-10">
      <div>
        {badge && <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-2">{badge}</p>}
        <h2 className="font-display text-2xl font-bold">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      </div>
      {to && (
        <Link to={to} className="group flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors shrink-0 ms-4">
          See {title} <ArrowRight className="h-4 w-4 rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
        </Link>
      )}
    </div>
  );
}

// Cairo "business day" key, treating 4am as the start of the day rather
// than midnight — so a selection made at 1am still counts as "yesterday".
// Works off the device clock's real UTC instant (reliable everywhere),
// just formatted in Cairo wall-clock time, so it doesn't matter where the
// visitor physically is or what timezone their device is set to.
function getCairoDayKey(cutoverHour = 4): string {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Cairo',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false,
  }).formatToParts(now);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '0';
  const year = parseInt(get('year'), 10);
  const month = parseInt(get('month'), 10);
  const day = parseInt(get('day'), 10);
  let hour = parseInt(get('hour'), 10);
  if (hour === 24) hour = 0; // some locales format midnight as "24"

  const cairoDate = new Date(year, month - 1, day);
  if (hour < cutoverHour) cairoDate.setDate(cairoDate.getDate() - 1);

  const y = cairoDate.getFullYear();
  const m = String(cairoDate.getMonth() + 1).padStart(2, '0');
  const d = String(cairoDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Deterministic 32-bit string hash (not cryptographic — just needs to be
// stable and well-distributed enough for picking a shuffle offset)
function hashSeed(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return h >>> 0;
}

// mulberry32 — tiny deterministic PRNG, same seed always produces the same
// output, which is the whole point (no external dependency needed for this)
function seededRandom(seed: number): number {
  let t = (seed += 0x6D2B79F5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

// `sectionKey` salts the seed per-section so Best Sellers, Featured, etc.
// don't all happen to land on the same offset on a given day — each
// section still changes once daily, just not in lockstep with the others.
function useRotatingProducts(pool: Product[], count: number, sectionKey: string) {
  const dayKey = getCairoDayKey(4);
  const offset = useMemo(() => {
    if (pool.length <= count) return 0;
    return Math.floor(seededRandom(hashSeed(`${dayKey}:${sectionKey}`)) * pool.length);
  }, [pool.length, count, dayKey, sectionKey]);

  if (!pool.length) return [];
  const slice: Product[] = [];
  for (let i = 0; i < count; i++) slice.push(pool[(offset + i) % pool.length]);
  return slice;
}

// ── Teaser grid (small square images) ────────────────────────────
function TeaserGrid({ images, accentColor = 'border-border/40' }: { images: string[]; accentColor?: string }) {
  if (!images.length) return null;
  return (
    <div className="flex gap-2 mt-5 flex-wrap">
      {images.map((src, i) => (
        <div key={i}
          className={`overflow-hidden rounded-xl border ${accentColor} bg-secondary/20 flex-shrink-0`}
          style={{ width: '18%', minWidth: 52, aspectRatio: '1' }}>
          <img src={src} alt={`Preview ${i + 1}`}
            className="w-full h-full object-cover opacity-70 hover:opacity-100 transition-opacity duration-500" />
        </div>
      ))}
    </div>
  );
}

// ── Gift Card exclusive card ──────────────────────────────────────
function GiftCardExclusiveCard({
  tier, product, onAddToCart,
}: {
  tier: typeof GIFT_TIERS[0];
  product?: Product;
  onAddToCart: (p: Product) => void;
}) {
  const imgSrc = assetUrl(tier.front);
  return (
    <div className="glass rounded-2xl overflow-hidden border border-border/40 hover:border-primary/30 transition-all duration-300 flex flex-col group">
      {/* Card image */}
      <div className="relative aspect-square overflow-hidden bg-secondary/20">
        {imgSrc ? (
          <img src={imgSrc} alt={tier.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gift className="h-12 w-12 text-muted-foreground/20" />
          </div>
        )}
        {/* Tier badge */}
        <div className="absolute top-2 left-2">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border"
            style={{ background: `${tier.accent}20`, color: tier.accent, borderColor: `${tier.accent}40` }}>
            {tier.label}
          </span>
        </div>
      </div>
      {/* Info */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-sm font-bold leading-tight">{tier.name}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">{tier.tagline}</p>
        {product ? (
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm font-bold text-primary">EGP {product.price.toLocaleString()}</span>
            <button
              onClick={() => onAddToCart(product)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
            >
              <ShoppingCart className="h-3 w-3" /> Add
            </button>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: tier.accent }} />
            Pricing coming soon
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════
const Index = () => {
  const { t } = useTranslation();
  const { data: allProducts = [], isLoading } = useProducts();
  const { cart, cartOpen, openCart, closeCart, addToCart, updateQty, removeItem, clearCart } = useCart();

  // ── Product pools ─────────────────────────────────────────────
  const featuredProducts = useMemo(() => allProducts.filter(p => p.tags?.includes('Featured')), [allProducts]);
  const bestSellersPool  = useMemo(() => allProducts.filter(p => p.tags?.includes('Best Seller')), [allProducts]);
  const newArrivalsPool  = useMemo(() => [...allProducts], [allProducts]);
  const explorePool      = useMemo(() => {
    const cats = Array.from(new Set(allProducts.map(p => p.category)));
    return cats.flatMap(cat => allProducts.filter(p => p.category === cat));
  }, [allProducts]);
  const catchItProducts  = useMemo(() =>
    allProducts.filter(p => p.inventoryCount !== undefined && p.inventoryCount !== null && p.inventoryCount <= 2 && p.inventoryCount > 0),
    [allProducts]);
  // "Most Viewed" — no real per-product view-count data is tracked yet
  // (analytics.ts only forwards view events to GA4, nothing queryable back
  // into the storefront), so this pool draws from the full catalogue and
  // relies on the same daily rotation as every other showcase section below.
  const mostViewedPool   = useMemo(() => [...allProducts], [allProducts]);
  // Same "is this actually a deal" check ProductCard already uses to show
  // the strikethrough price / promo badge — so this section only ever
  // shows products that visibly look discounted once you click in.
  const dealsPool = useMemo(() =>
    allProducts.filter(p =>
      (p.compareAtPrice && p.compareAtPrice > p.price) ||
      (p.promoLabel && (!p.promoExpires || new Date(p.promoExpires) > new Date()))
    ),
    [allProducts]);

  // ── Match gift card products from DB ─────────────────────────
  const findGiftProduct = (tier: typeof GIFT_TIERS[0]): Product | undefined =>
    allProducts.find(p =>
      p.title?.toLowerCase().includes(tier.id) ||
      p.title?.toLowerCase().includes(tier.name.toLowerCase().split(' ')[0])
    );

  // ── Rotating slices ───────────────────────────────────────────
  const bestSellers  = useRotatingProducts(bestSellersPool, 4, 'best-sellers');
  const featuredSlice = useRotatingProducts(featuredProducts, 4, 'featured');
  const newArrivals  = useRotatingProducts(newArrivalsPool, 8, 'new-arrivals');
  const exploreSlice = useRotatingProducts(explorePool, 4, 'explore');
  const catchSlice   = useRotatingProducts(catchItProducts, 8, 'catch-it');
  const dealsSlice   = useRotatingProducts(dealsPool, 4, 'deals');
  const mostViewedSlice = useRotatingProducts(mostViewedPool, 4, 'most-viewed');

  return (
    <div className="min-h-screen bg-background">
      <Navbar cartCount={cart.reduce((s, i) => s + i.quantity, 0)} onCartOpen={openCart} />
      <CartDrawer open={cartOpen} onClose={closeCart} items={cart} onUpdateQty={updateQty} onRemove={removeItem} onClearCart={clearCart} />

      <HeroSection />
      <AnnouncementBar />

      {/* ── Why Us Trust Bar ─────────────────────────────────────── */}
      <div className="border-y border-border/40 bg-card/30 backdrop-blur-sm py-5 my-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: <Zap className="h-4 w-4 text-yellow-400" />, title: 'Fast Shipping', sub: '1–3 business days' },
              { icon: <Shield className="h-4 w-4 text-emerald-400" />, title: 'Secure Payment', sub: 'InstaPay & COD' },
              { icon: <Headphones className="h-4 w-4 text-blue-400" />, title: '24/7 Support', sub: 'WhatsApp anytime' },
              { icon: <Star className="h-4 w-4 text-amber-400" />, title: 'Quality Guarantee', sub: 'Curated products only' },
            ].map(b => (
              <div key={b.title} className="flex items-center gap-2.5 py-1">
                <div className="shrink-0 h-8 w-8 rounded-lg bg-secondary/60 flex items-center justify-center">{b.icon}</div>
                <div>
                  <p className="text-xs font-semibold text-foreground leading-tight">{b.title}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Shop by Category ─────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="text-center mb-4">
          <h2 className="font-display text-lg font-bold">{t('home.shopByCategory')}</h2>
        </div>
        <div className="flex gap-3 overflow-x-auto justify-start pb-2 snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0 md:flex-wrap md:justify-center md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CATEGORY_TREE.map(cat => (
            <Link
              key={cat.label}
              to={cat.to}
              className="group shrink-0 snap-start flex flex-col items-center gap-2 w-20 sm:w-24"
            >
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-secondary/50 border border-border/60 flex items-center justify-center text-primary group-hover:border-primary/40 group-hover:bg-primary/5 transition-colors mx-auto">
                {HOME_CATEGORY_ICONS[cat.label] || <Tag className="h-6 w-6 text-muted-foreground" />}
              </div>
              <span className="text-[11px] text-center text-muted-foreground group-hover:text-foreground transition-colors leading-tight line-clamp-2">
                {cat.label}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── 1. Best Sellers ──────────────────────────────────────── */}
      {(isLoading || bestSellersPool.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <SectionHeader badge={t('home.badgeCommunityFavourites')} title={t('home.bestSellers')} subtitle={t('home.bestSellersSubtitle')} to="/best-sellers" />
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoading
              ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              : bestSellers.map((p, i) => <ProductCard key={p.id} product={p} onAddToCart={addToCart} index={i} />)}
          </div>
        </section>
      )}

      {/* ── 2. Featured Products ─────────────────────────────────── */}
      {(isLoading || featuredProducts.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <SectionHeader badge={t('home.badgeCuratedForYou')} title={t('home.featuredProducts')} subtitle={t('home.featuredSubtitle')} to="/products" />
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoading
              ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              : featuredSlice.map((p, i) => <ProductCard key={`featured-${p.id}-${i}`} product={p} onAddToCart={addToCart} index={i} />)}
          </div>
        </section>
      )}

      {/* ── 3. New Arrivals ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <SectionHeader badge={t('home.badgeJustLanded')} title={t('home.newArrivals')} subtitle={t('home.newArrivalsSubtitle')} to="/products" />
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading
            ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            : newArrivals.map((p, i) => <ProductCard key={`new-${p.id}-${i}`} product={p} onAddToCart={addToCart} index={i} />)}
        </div>
      </section>

      {/* ── 4. Explore ───────────────────────────────────────────── */}
      {(isLoading || explorePool.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <SectionHeader badge={t('home.badgeDiscover')} title={t('home.explore')} subtitle={t('home.exploreSubtitle')} to="/products" />
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {isLoading
              ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              : explorePool.slice(0, 12).map((p, i) => <ProductCard key={`explore-${p.id}-${i}`} product={p} onAddToCart={addToCart} index={i} />)}
          </div>
        </section>
      )}

      {/* ── Curated Collections ───────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <SectionHeader badge={t('home.badgeHandpickedForYou')} title={t('home.curatedCollections')} subtitle={t('home.curatedSubtitle')} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: t('home.essentials'), emoji: "⚡", desc: t('home.essentialsDesc'), color: "border-blue-500/20 bg-blue-500/5", accent: "text-blue-400", to: "/collection/essentials" },
            { label: t('home.trendingNow'), emoji: "🔥", desc: t('home.trendingDesc'), color: "border-amber-500/20 bg-amber-500/5", accent: "text-amber-400", to: "/collection/trending" },
            { label: t('home.giftReady'), emoji: "🎁", desc: t('home.giftDesc'), color: "border-emerald-500/20 bg-emerald-500/5", accent: "text-emerald-400", to: "/collection/gift-ready" },
          ].map(c => (
            <Link to={c.to} key={c.label} className={`group glass rounded-2xl p-6 border ${c.color} hover:scale-[1.02] transition-transform duration-200 flex flex-col gap-3`}>
              <div className="text-3xl">{c.emoji}</div>
              <div>
                <p className={`font-bold text-lg ${c.accent}`}>{c.label}</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.desc}</p>
              </div>
              <div className={`flex items-center gap-1 text-xs font-semibold ${c.accent} mt-auto`}>
                {t('home.shopCollection')} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Today's Deals ─────────────────────────────────────────── */}
      {(isLoading || dealsPool.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <SectionHeader badge={t('home.todaysDealsBadge')} title={t('home.todaysDeals')} subtitle={t('home.todaysDealsSubtitle')} to="/products" />
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoading
              ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              : dealsSlice.map((p, i) => <ProductCard key={`deal-${p.id}-${i}`} product={p} onAddToCart={addToCart} index={i} />)}
          </div>
        </section>
      )}

      {/* ── Most Viewed ──────────────────────────────────────────── */}
      {(isLoading || mostViewedPool.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <SectionHeader badge={t('home.badgeTrendingWithShoppers')} title={t('home.mostViewed')} subtitle={t('home.mostViewedSubtitle')} to="/products" />
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoading
              ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              : mostViewedSlice.map((p, i) => <ProductCard key={`viewed-${p.id}-${i}`} product={p} onAddToCart={addToCart} index={i} />)}
          </div>
        </section>
      )}

      {/* ── 5. Catch It Before It's Gone ─────────────────────────── */}
      {(isLoading || catchItProducts.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="relative rounded-2xl overflow-hidden border border-destructive/20 bg-destructive/5 p-6 sm:p-10">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-destructive/5 blur-3xl pointer-events-none" />
            <div className="relative z-10 mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-destructive font-semibold mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-destructive animate-pulse" /> {t('home.lowStockAlert')}
              </p>
              <h2 className="font-display text-2xl font-bold">{t('home.catchItBefore')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t('home.catchItSubtitle')}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
              {isLoading
                ? <div className="col-span-4 flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                : catchSlice.length === 0
                  ? <div className="col-span-4 py-8 text-center text-muted-foreground text-sm">{t('home.allStockedUp')}</div>
                  : catchSlice.map((p, i) => <ProductCard key={`catch-${p.id}-${i}`} product={p} onAddToCart={addToCart} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── Coming Soon + Release Radar + Special Request ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* Special Request */}
          <div className="glass rounded-2xl p-8 flex flex-col justify-between min-h-[280px] relative overflow-hidden border border-[#25D366]/20 bg-[#25D366]/5 group">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#25D366]/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[0.25em] text-[#25D366] font-semibold mb-3">Can't Find What You Need?</p>
              <h2 className="font-display text-xl font-bold mb-3">Special Request</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">Drop us a message on WhatsApp — we'll source it for you. No request is too specific.</p>
            </div>
            <div className="relative z-10 mt-6 flex flex-col gap-3">
              <a href="https://wa.me/message/ACL5MCRFCEGOD1" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl bg-[#25D366] text-black text-sm font-bold hover:bg-[#1ebe5a] transition-colors">
                <MessageCircle className="h-4 w-4" /> Send Special Request
              </a>
              <p className="text-xs text-center text-muted-foreground">+20 1037530503 · Usually replies within 1h</p>
            </div>
          </div>


          {/* Release Radar */}
          <div className="glass rounded-2xl p-8 flex flex-col justify-between min-h-[280px] relative overflow-hidden group border border-primary/10">
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-chart-3/10 blur-3xl group-hover:bg-chart-3/20 transition-all duration-700" />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[0.25em] text-chart-3 font-semibold mb-3">Drop Calendar</p>
              <h2 className="font-display text-xl font-bold mb-3">Importing Original Brands 📡</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">Be the first to know about upcoming launches. Every drop, tracked and ready.</p>
              {RELEASE_RADAR_IMGS.length > 0 && (
                <TeaserGrid images={RELEASE_RADAR_IMGS} accentColor="border-chart-3/20" />
              )}
            </div>
            <div className="relative z-10 mt-6">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-chart-3/10 border border-chart-3/20 w-fit">
                <span className="h-3 w-3 rounded-full border-2 border-chart-3 border-t-transparent animate-spin" />
                <span className="text-xs font-semibold text-chart-3">Loading incoming brands...</span>
              </div>
            </div>
          </div>


          {/* Coming Soon */}
          <div className="glass rounded-2xl p-8 flex flex-col justify-between min-h-[280px] relative overflow-hidden group border border-primary/10">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/10 blur-3xl group-hover:bg-primary/20 transition-all duration-700" />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-3">What's Next</p>
              <h2 className="font-display text-xl font-bold mb-3">Coming Soon</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">New collections, exclusive drops, and curated experiences are on their way.</p>
              {COMING_SOON_IMGS.length > 0 && (
                <TeaserGrid images={COMING_SOON_IMGS} accentColor="border-primary/20" />
              )}
            </div>
            <div className="relative z-10 mt-6 flex items-center gap-3">
              <div className="flex gap-1.5">
                {[...Array(3)].map((_, i) => (
                  <span key={i} className="h-2 w-2 rounded-full bg-primary/40 animate-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">Loading the future…</span>
            </div>
          </div>

        </div>
      </section>

      {/* ── 6. Wall of Love + Share Your Experience ──────────────── */}
      <CustomerFeedback />

      {/* ── 4. Ziko Exclusive — Gift Cards ───────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="relative rounded-2xl overflow-hidden border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-chart-3/5 p-6 sm:p-10">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
          <div className="relative z-10 mb-8 flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-2 flex items-center gap-2">
                <Gift className="h-3.5 w-3.5" /> Only at ZIKO
              </p>
              <h2 className="font-display text-base font-bold">Ziko Exclusive</h2>
              <p className="text-sm text-muted-foreground mt-1">Five tiers of premium digital gift cards — from quiet exclusivity to absolute sovereignty</p>
            </div>
            <Link to="/gift-cards" className="group flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors shrink-0 ms-4">
              See All <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 relative z-10">
              {GIFT_TIERS.map(tier => (
                <GiftCardExclusiveCard
                  key={tier.id}
                  tier={tier}
                  product={findGiftProduct(tier)}
                  onAddToCart={addToCart}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
